/**
 * Oak saplings (vanilla SaplingBlock + the plain oak TreeFeature), DOM-free.
 *
 * The game has one sapling block, so growth is stage-less: vanilla needs two successful events (the first only
 * flips the block's `stage`), here each event grows the tree with chance 1/2, which has the same expected cost.
 *   - random tick: light >= 9 above, then a 1/7 chance per tick (SaplingBlock.randomTick)
 *   - bone meal: 45 % success (SaplingBlock.isBoneMealSuccess), consumed even on failure
 *
 *   growOakTree(w, x, y, z, rnd)   replace the sapling at (x, y, z) with a 4..6 high oak; false when there is no room
 */

import {
  AIR, COARSE_DIRT_ID, DIRT_ID, FARMLAND_DRY_ID, FARMLAND_ID, GRASS_ID, IS_OPAQUE, LEAVES_ID, LOG_ID, MOSS_ID, MUD_ID,
  MYCELIUM_ID, OAK_SAPLING_ID, PODZOL_ID, ROOTED_DIRT_ID, RENDER_SHAPE, RenderShape,
} from '../../blocks';

export interface SaplingWorld {
  getBlock(x: number, y: number, z: number): number;
  setBlock(x: number, y: number, z: number, id: number): void;
  /** Can this cell be edited right now (its chunk is loaded)? Absent = always. A tree needs its whole footprint. */
  canSet?(x: number, y: number, z: number): boolean;
}

/** Vanilla `#dirt` + farmland: what a sapling may stand on. */
const SOIL = new Set<number>([DIRT_ID, GRASS_ID, PODZOL_ID, MYCELIUM_ID, COARSE_DIRT_ID, ROOTED_DIRT_ID, MOSS_ID, MUD_ID, FARMLAND_ID, FARMLAND_DRY_ID]);
export const canSaplingStand = (below: number): boolean => SOIL.has(below);

/** Random-tick chance (SaplingBlock: nextInt(7) == 0). */
export const SAPLING_TICK_CHANCE = 1 / 7;
/** Bone-meal success chance (vanilla 0.45). */
export const SAPLING_BONEMEAL_CHANCE = 0.45;
/** Minimum light above the sapling for it to grow. */
export const SAPLING_MIN_LIGHT = 9;

const replaceable = (id: number): boolean => id === AIR || id === LEAVES_ID || (id > 0 && IS_OPAQUE[id] === 0 && RENDER_SHAPE[id] !== RenderShape.Cube);

/** Is there room for a trunk of height `h` plus the canopy above `(x, y, z)`? */
function hasRoom(w: SaplingWorld, x: number, y: number, z: number, h: number): boolean {
  for (let dy = 1; dy < h; dy++) if (!replaceable(w.getBlock(x, y + dy, z))) return false;
  // canopy columns around the top are only checked at the trunk's own column (leaves overwrite flora like vanilla)
  return replaceable(w.getBlock(x, y + h, z));
}

/** Oak canopy layers, top to bottom: [dy above the top log, radius, corners always cut]. */
const CANOPY: ReadonlyArray<readonly [number, number, boolean]> = [[1, 1, true], [0, 1, false], [-1, 2, false], [-2, 2, false]];

/**
 * Grow the oak: trunk `h` = 4 + rnd(3) logs (vanilla base height 4, +0..2), a blob canopy of radius 2 (layers
 * below the top) and 1 (the top two), outer corners of the wide layers kept half the time.
 */
export function growOakTree(w: SaplingWorld, x: number, y: number, z: number, rnd: () => number): boolean {
  if (w.getBlock(x, y, z) !== OAK_SAPLING_ID) return false;
  if (!canSaplingStand(w.getBlock(x, y - 1, z))) return false;
  const h = 4 + Math.floor(rnd() * 3);
  if (!hasRoom(w, x, y, z, h)) return false;
  if (w.canSet) for (const dy of [0, h + 1]) for (const dx of [-2, 2]) for (const dz of [-2, 2]) if (!w.canSet(x + dx, y + dy, z + dz)) return false;
  const topLog = y + h - 1;
  for (const [dy, r, cut] of CANOPY) {
    const ly = topLog + dy;
    for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) {
      if (dx === 0 && dz === 0 && ly <= topLog) continue; // trunk column
      const corner = Math.abs(dx) === r && Math.abs(dz) === r;
      if (corner && r > 0 && (cut || rnd() < 0.5)) continue;
      if (replaceable(w.getBlock(x + dx, ly, z + dz))) w.setBlock(x + dx, ly, z + dz, LEAVES_ID);
    }
  }
  for (let dy = 0; dy < h; dy++) w.setBlock(x, y + dy, z, LOG_ID);
  // vanilla turns the grass under a trunk to dirt
  if (w.getBlock(x, y - 1, z) === GRASS_ID) w.setBlock(x, y - 1, z, DIRT_ID);
  return true;
}

/** `events` successful growth events (random ticks or bone meals) on the sapling: true when the tree grew. */
export function saplingEvents(w: SaplingWorld, x: number, y: number, z: number, events: number, rnd: () => number): boolean {
  for (let i = 0; i < events; i++) {
    if (rnd() < 0.5) return growOakTree(w, x, y, z, rnd);
  }
  return false;
}
